import { useLocation } from "react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import CredentialsForm from "@/components/Form/Form";
import Checkbox from "@/components/checkbox/Checkbox";
import CredentialsHeader from "@/components/header/Header";
import ButtonContainer from "@/components/button-container";

import { genericService } from "@/services/GenericService";

import { tryCatch } from "@/utils/try-catch";
import { FormError } from "@/models/site-credentials/common/FormError";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import SimpleErrorMessage from "@/components/error-message/new-error-message";

type KeepMeLoggedInForm = {
    dontShowAgain: boolean;
    keepMeLoggedIn?: boolean;
}

type KeepMeLoggedInFormResponse = {
    nextStep: string;
    properties: {
        returnUrl: string
        ssoSessionId: string
        ssoSessionLifetimeInDays: string
    };
};

type Form = KeepMeLoggedInForm & FormError;

export default function KeepMeLoggedIn() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const location = useLocation<KeepMeLoggedInFormResponse>();
    const { handleResponse } = useApiResponseHandler();

    const [dontShowAgain, setDontShowAgain] = useState(false);

    const formState = useForm<Form>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
    });

    const nextStep = "api/sso/keep-me-logged-in" 

    const {
        handleSubmit,
        setError,
        setValue,
        clearErrors,
        formState: { errors },
    } = formState;

    const onSubmit: SubmitHandler<KeepMeLoggedInForm> = async (data) => {

        setLoading(true);

        const { data: response, error } = await tryCatch(genericService.post<KeepMeLoggedInFormResponse, KeepMeLoggedInForm>(
            nextStep,
            {
                keepMeLoggedIn: data.keepMeLoggedIn,
                dontShowAgain: data.dontShowAgain,
            }
        ));

        handleResponse(
            response,
            {
                setError
            },
            error
        );

        setLoading(false);
    };

    const handleDontShowAgainChange = () => {
        const newState = !dontShowAgain;
        setDontShowAgain(newState);
        setValue("dontShowAgain", newState);
    };

    useEffect(() => {
        setValue("dontShowAgain", false)
    }, [setValue])

    const handleChooseOption = (value: boolean) => {
        setValue("keepMeLoggedIn", value);
        clearErrors();
        handleSubmit(onSubmit)();
    };

    return (
        <CredentialsForm
            id="KeepMeLoggedInForm"
            autoComplete="on"
            form={formState}
            onSubmit={(e) => {
                clearErrors();
                handleSubmit(onSubmit)(e);
            }}
        >
            <CredentialsHeader
                title={t("keepMeLoggedIn.title")}
                subtitle={t("keepMeLoggedIn.subtitle")}
                showLoading={loading}
                showBackButton
                previousStepBackXTimes={1}
            />

            <div className="flex flex-col justify-center mt-2 items-center gap-4 ">
                <div className="flex w-full">
                    <Checkbox
                        ariaLabel={t("keepMeLoggedIn.dontShowAgain")}
                        onClick={handleDontShowAgainChange}
                    />
                    <div className="text-sm text-[var(--on-surface-default)] content-center">
                        <span aria-hidden={true}>{t("keepMeLoggedIn.dontShowAgain")}</span>
                    </div>
                </div>
            </div>

            {errors.submitError?.message &&
                <SimpleErrorMessage
                    id={"error-message"}
                    error={errors.submitError?.message}
                />
            }

            <ButtonContainer className="gap-0">
                <Button
                    variant={"link"}
                    type="button"
                    className="pb-0"
                    onClick={() => handleChooseOption(false)}
                >
                    {t("keepMeLoggedIn.no")}
                </Button>

                <Button
                    variant={"default"}
                    type="button"
                    onClick={() => handleChooseOption(true)}
                >
                    {t("keepMeLoggedIn.yes")}
                </Button>
            </ButtonContainer>
        </CredentialsForm>
    );
}